// src/applog/datom-types.ts
import { FormatRegistry, Type } from "@sinclair/typebox";
import { TypeCompiler } from "@sinclair/typebox/compiler";
var Nullable = (schema) => Type.Union([schema, Type.Null()]);
var EntityID_LENGTH = 7;
var isCID = /^(k51qz|baguq)[0-9a-z]{56,57}$/;
var isShortHash = /^[0-9A-Fa-f]{7,8}$/g;
FormatRegistry.Set("EntityID", (value) => !!value.match(isShortHash) || !!value.match(isCID));
var EntityID = Type.String();
var isEncryptedApplog = (l) => l?.enc instanceof Uint8Array;
FormatRegistry.Set("CID", (value) => !!value.match(isCID));
var CIDTB = Type.String({ pattern: isCID.source });
var isURL = /^http([s]?):\/\/.*\..*/;
FormatRegistry.Set("URL", (value) => !!value.match(isURL));
var URL = Type.String({ pattern: isURL.source });
var JsonValueTB = Type.Recursive(
  (This) => Type.Union([
    Type.String(),
    Type.Number(),
    Type.Boolean(),
    Type.Null(),
    Type.Array(This),
    Type.Record(Type.String(), This)
  ])
);
var AppLogNoCidTB = Type.Object({
  en: EntityID,
  // EntityID
  at: Type.String(),
  // Attribute
  vl: JsonValueTB,
  // ApplogValue (JSON-serializable: primitives, arrays, objects)
  ts: Type.String(),
  // Timestamp
  ag: Type.String(),
  // AgentHash
  pv: Nullable(CIDTB)
  // CidString
});
var AppLogNoCidTBC = TypeCompiler.Compile(AppLogNoCidTB);
var getApplogNoCidTypeErrors = (obj) => Array.from(AppLogNoCidTBC.Errors(obj));
var isValidApplogNoCid = AppLogNoCidTBC.Check.bind(AppLogNoCidTBC);
var AppLogTB = Type.Composite([
  Type.Object({
    cid: CIDTB
  }),
  AppLogNoCidTB
]);
var AppLogTBC = TypeCompiler.Compile(AppLogTB);
var getApplogTypeErrors = (obj) => Array.from(AppLogTBC.Errors(obj));
var isValidApplog = AppLogTBC.Check.bind(AppLogTBC);

// src/ipfs/ipfs-utils.ts
import * as dagJson from "@ipld/dag-json";
import { sha256 } from "@noble/hashes/sha2.js";
import { Logger } from "besonders-logger";
import { CID, digest as Digest } from "multiformats";
import { encode as multiformatsEncode } from "multiformats/block";
import { base36 } from "multiformats/bases/base36";
import { bases } from "multiformats/basics";
import { sha256 as sha265Hasher } from "multiformats/hashes/sha2";
var { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO);
var MULTICODEC_IPNS_KEY = 114;
function prepareForPub(log, without = ["cid"]) {
  if (!log) throw ERROR("falsy log", log);
  let cid = log.cid;
  if (isEncryptedApplog(log)) {
    if (!cid) cid = getCidSync(encodeBlock(log).bytes).toString();
    WARN("preparing an encrypted applog - really?");
    return { log, cid };
  }
  const logWithout = {};
  for (let [key, val] of Object.entries(log)) {
    if (val === void 0) {
      WARN(`log.${key} is undefined, which is not allowed - encoding as null`, log);
      val = null;
    }
    if (!without.includes(key)) {
      logWithout[key] = val;
    } else {
      VERBOSE("excluding app log", { key, val });
    }
  }
  return { log: logWithout, cid };
}
function encodeApplogAndGetCid(log) {
  return getCidSync(encodeApplog(log).bytes);
}
function encodeApplog(log) {
  return encodeBlock(prepareForPub(log)?.log);
}
function getCidSync(bytes) {
  const hash = sha256(bytes);
  const digest = Digest.create(sha265Hasher.code, hash);
  const cid = CID.create(1, dagJson.code, digest);
  VERBOSE(`[getCidSync]`, { bytes, hash, digest, cid });
  return cid;
}
function encodeBlock(jsonObject) {
  DEBUG("[encodeBlock]", jsonObject);
  try {
    const byteView = dagJson.encode(jsonObject);
    return { bytes: byteView, cid: getCidSync(byteView) };
  } catch (err) {
    throw ERROR("[encodeBlock] failed to encode:", jsonObject, err);
  }
}
async function encodeBlockOriginal(jsonObject) {
  const encoded = await multiformatsEncode({ value: jsonObject, codec: dagJson, hasher: sha265Hasher });
  const syncVariant = encodeBlock(jsonObject);
  if (syncVariant.cid.toString() !== encoded.cid.toString()) {
    ERROR(`[encodeBlockOriginal] sync cid mismatch`, { jsonObject, encoded, syncVariant });
  }
  return encoded;
}
var allBasesDecoder = Object.values(bases).map((base) => base.decoder).reduce((combined, decoder) => combined ? combined.or(decoder) : decoder);
function parseCID(cidString) {
  return CID.parse(cidString, allBasesDecoder);
}
function tryParseCID(cidString) {
  let cid = null;
  let errors = [];
  try {
    cid = parseCID(cidString);
  } catch (err) {
    VERBOSE(`[retrieveThread] couldn't parse pubID with default base`);
    errors.push(err);
  }
  if (!cid) {
    try {
      cid = CID.parse(cidString, base36);
    } catch (err) {
      VERBOSE(`[retrieveThread] couldn't parse pubID with base36`);
      errors.push(err);
    }
  }
  return {
    cid,
    errors: cid ? null : errors,
    // we only care about errors if we failed to parse
    isIpns: cid && isIpnsKeyCid(cid)
  };
}
function isIpnsKeyCid(cid) {
  return cid.code === MULTICODEC_IPNS_KEY;
}
function cidToString(cid) {
  if (cid.code == MULTICODEC_IPNS_KEY) {
    return toIpnsString(cid);
  } else {
    return cid.toString();
  }
}
function toIpnsString(cid) {
  if (cid.code !== MULTICODEC_IPNS_KEY) throw ERROR(`Not an IPNS cid (${cid.code}):`, cid.toString());
  return cid.toString(base36);
}
function ensureValidCIDinstance(cidOrStringA) {
  return typeof cidOrStringA === "string" ? parseCID(cidOrStringA) : typeof cidOrStringA.toV1 != "function" ? CID.decode(cidOrStringA.bytes) : cidOrStringA;
}
function areCidsEqual(cidOrStringA, cidOrStringB) {
  if (!cidOrStringA || !cidOrStringB) throw new Error(`[areCidsEqual] invalid params: ${cidOrStringA}, ${cidOrStringB}`);
  if (cidOrStringA === cidOrStringB) return true;
  const cidA = ensureValidCIDinstance(cidOrStringA);
  const cidB = ensureValidCIDinstance(cidOrStringB);
  return cidA.toV1().toString() === cidB.toV1().toString();
}
function containsCid(list, needle) {
  if (list instanceof Set) return list.has(typeof needle === "string" ? needle : needle.toV1().toString());
  return list.some((cidOrString) => areCidsEqual(cidOrString, needle));
}

export {
  Nullable,
  EntityID_LENGTH,
  EntityID,
  isEncryptedApplog,
  CIDTB,
  URL,
  JsonValueTB,
  AppLogNoCidTB,
  AppLogNoCidTBC,
  getApplogNoCidTypeErrors,
  isValidApplogNoCid,
  AppLogTB,
  AppLogTBC,
  getApplogTypeErrors,
  isValidApplog,
  MULTICODEC_IPNS_KEY,
  prepareForPub,
  encodeApplogAndGetCid,
  encodeApplog,
  getCidSync,
  encodeBlock,
  encodeBlockOriginal,
  parseCID,
  tryParseCID,
  isIpnsKeyCid,
  cidToString,
  toIpnsString,
  ensureValidCIDinstance,
  areCidsEqual,
  containsCid
};
//# sourceMappingURL=chunk-KPMINROC.js.map